/**
 * Public unsubscribe landing page — /unsubscribe/:token
 *
 * The backend endpoint returns HTML directly for GET, but we also expose a
 * React shell for the user's Manage-Preferences click-through so they can
 * choose "unsubscribe from all" or a specific category on the SAME page.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import BrandMark from "@/components/BrandMark";

export default function Unsubscribe() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const scope = params.get("scope") || "category";
  const category = params.get("category") || "";
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/notifications/unsubscribe/${token}?scope=${scope}&category=${encodeURIComponent(category)}`;
    // The backend returns HTML directly. For the SPA experience we fetch JSON via GET
    // then render our own confirmation. We'll fetch as HTML text and just show a friendly page.
    fetch(url, { method: "GET" }).then(async (r) => {
      if (r.ok) {
        setStatus("done");
        setMessage(scope === "all"
          ? "You've been unsubscribed from all Career OS marketing emails."
          : `You've been unsubscribed from ${category || "this category"} emails.`);
      } else {
        setStatus("error");
        setMessage("This unsubscribe link is invalid or expired.");
      }
    }).catch(() => {
      setStatus("error");
      setMessage("Something went wrong. Try again later.");
    });
  }, [token, scope, category]);

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6" data-testid="unsubscribe-page">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
        <BrandMark size={48} className="rounded-lg mx-auto mb-4" />
        <h1 className="font-display text-2xl font-bold mb-2" data-testid="unsub-title">
          {status === "processing" && "Updating your preferences…"}
          {status === "done" && "Preferences updated"}
          {status === "error" && "Something went wrong"}
        </h1>
        <p className="text-sm text-muted-foreground mb-6" data-testid="unsub-message">{message}</p>
        <a href="/app/settings" className="inline-block bg-primary text-primary-foreground px-5 py-2.5 rounded-md font-semibold text-sm" data-testid="unsub-manage-btn">Manage all preferences</a>
      </div>
    </div>
  );
}
